import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc,
  deleteDoc,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { networkMonitor } from './networkMonitor';
import {
  addOfflineExpense,
  updateOfflineExpense,
  deleteOfflineExpense,
  seedFirestoreExpenseForOfflineUpdate,
  getOfflineExpenses,
  markExpenseSynced,
  markExpenseSyncFailed,
  addPendingFirestoreExpenseDelete,
  removePendingFirestoreExpenseDelete,
  getPendingFirestoreExpenseDeleteSet,
  type OfflineExpense,
} from './offlineExpenses';
import { syncQueue } from './syncQueue';
import { getExchangeRate } from '@/utils/currency';

const PRE_TRIP_DATE_BUFFER_DAYS = 365; // Allow expenses up to 12 months before trip start (advanced bookings)
const POST_TRIP_DATE_BUFFER_DAYS = 90; // Allow expenses up to 90 days after trip end (settlements/refunds)

const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getAllowedExpenseDateWindow = (tripStartDate: Date, tripEndDate: Date) => {
  const normalizedTripStart = normalizeDate(tripStartDate);
  const normalizedTripEnd = normalizeDate(tripEndDate);

  const minDate = new Date(normalizedTripStart);
  minDate.setDate(minDate.getDate() - PRE_TRIP_DATE_BUFFER_DAYS);

  const maxDate = new Date(normalizedTripEnd);
  maxDate.setDate(maxDate.getDate() + POST_TRIP_DATE_BUFFER_DAYS);

  return {
    minDate: normalizeDate(minDate),
    maxDate: normalizeDate(maxDate),
    normalizedTripStart,
    normalizedTripEnd,
  };
};

export type ExpenseCategory = 'flight' | 'lodging' | 'food' | 'transport' | 'activity' | 'shopping' | 'other';
export type PaymentMethod = 'cash' | 'card' | 'debit' | 'mobile' | 'other';
export type SplitType = 'none' | 'equal' | 'custom';
export type ExpenseSource = 'ai_estimated' | 'user_entered' | 'imported_booking';

export type ExpenseSplit = {
  splitType: SplitType;
  splitAmong: string[]; // Array of userIds
  paidBy: string; // userId of person who paid
  splitAmounts?: Record<string, number>; // For custom splits: { userId: amount }
};

/**
 * Currency conversion snapshot - stores the rate used when expense was added
 * This allows accurate historical tracking even if rates change
 */
export type CurrencyConversion = {
  /** The currency the expense was originally in */
  fromCurrency: string;
  /** The trip's base currency it was converted to */
  toCurrency: string;
  /** The exchange rate used (multiply amount by this to get converted amount) */
  exchangeRate: number;
  /** The converted amount in the trip's base currency */
  convertedAmount: number;
  /** When the conversion rate was fetched */
  conversionDate: Date;
};

export type Expense = {
  id: string;
  tripId: string;
  amount: number;
  currency: string;
  category: ExpenseCategory;
  description: string;
  date: Date;
  receiptUrl?: string;
  /** Local file URI saved when caching trip for offline (not stored in Firestore). */
  receiptLocalUri?: string;
  location?: string;
  paymentMethod?: PaymentMethod;
  merchant?: string;
  /**
   * Provenance of this value (used for budget transparency labels in the UI).
   * Older data may not have this field; consumers should treat `undefined` as `user_entered`.
   */
  source?: ExpenseSource;
  createdBy: string;
  /**
   * Optional: userId of the most recent editor (used for cross-device notifications).
   * If absent, consumers can fall back to createdBy.
   */
  updatedBy?: string;
  createdAt: Date;
  updatedAt: Date;
  // OCR fields (from receiptOcr function)
  ocrRawText?: string;
  status?: 'pending' | 'parsed' | 'failed';
  // Expense splitting fields
  split?: ExpenseSplit;
  /** 
   * Currency conversion snapshot - stores the rate used when expense was created.
   * Only present when expense currency differs from trip's base currency.
   */
  conversion?: CurrencyConversion;
};

type FirestoreCurrencyConversion = {
  fromCurrency: string;
  toCurrency: string;
  exchangeRate: number;
  convertedAmount: number;
  conversionDate: Timestamp | string;
};

async function filterExpensesPendingFirestoreDelete(tripId: string, expenses: Expense[]): Promise<Expense[]> {
  const pending = await getPendingFirestoreExpenseDeleteSet(tripId);
  if (pending.size === 0) return expenses;
  return expenses.filter((e) => !pending.has(e.id));
}

type FirestoreExpense = {
  amount: number;
  currency: string;
  category: string;
  description?: string;
  date: Timestamp | string;
  receiptUrl?: string;
  location?: string;
  paymentMethod?: string;
  merchant?: string;
  source?: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  ocrRawText?: string;
  status?: string;
  // Expense splitting fields
  split?: {
    splitType: string;
    splitAmong: string[];
    paidBy: string;
    splitAmounts?: Record<string, number>;
  };
  // Currency conversion snapshot
  conversion?: FirestoreCurrencyConversion;
};

// Convert Firestore expense to app Expense type
const convertFirestoreExpense = (id: string, data: FirestoreExpense): Expense => {
  const convertDate = (date: Timestamp | string | Date | undefined): Date => {
    if (!date) return new Date();
    if (date instanceof Timestamp) return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    return new Date();
  };

  // Convert currency conversion data
  const conversion: CurrencyConversion | undefined = data.conversion ? {
    fromCurrency: data.conversion.fromCurrency,
    toCurrency: data.conversion.toCurrency,
    exchangeRate: data.conversion.exchangeRate,
    convertedAmount: data.conversion.convertedAmount,
    conversionDate: convertDate(data.conversion.conversionDate),
  } : undefined;

  return {
    id,
    tripId: '', // Will be set by caller
    amount: data.amount || 0,
    currency: data.currency || 'USD',
    category: (data.category || 'other') as ExpenseCategory,
    description: data.description || '',
    date: convertDate(data.date),
    receiptUrl: data.receiptUrl,
    location: data.location,
    paymentMethod: data.paymentMethod as PaymentMethod | undefined,
    merchant: data.merchant,
    source: (data.source as ExpenseSource | undefined) ?? 'user_entered',
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    createdAt: convertDate(data.createdAt),
    updatedAt: convertDate(data.updatedAt),
    ocrRawText: data.ocrRawText,
    status: data.status as Expense['status'],
    split: data.split ? {
      splitType: data.split.splitType as SplitType,
      splitAmong: data.split.splitAmong || [],
      paidBy: data.split.paidBy,
      splitAmounts: data.split.splitAmounts,
    } : undefined,
    conversion,
  };
};

// Register sync handlers for expenses
syncQueue.registerHandler('create_expense', async (action) => {
  try {
    const { tripId, expense, offlineId } = action.payload as {
      tripId: string;
      expense: any;
      offlineId: string;
    };
    
    // Convert date string back to Date
    const expenseData = {
      ...expense,
      date: new Date(expense.date),
    };
    
    const syncedId = await createExpenseInFirestore(tripId, expenseData);
    await markExpenseSynced(tripId, offlineId, syncedId);
  } catch (error) {
    console.error('[Expenses] Error syncing create_expense:', error);
    const { tripId, offlineId } = action.payload as { tripId: string; offlineId: string };
    await markExpenseSyncFailed(tripId, offlineId, error instanceof Error ? error.message : 'Sync failed');
    throw error;
  }
});

syncQueue.registerHandler('update_expense', async (action) => {
  try {
    const { tripId, expenseId, updates } = action.payload as {
      tripId: string;
      expenseId: string;
      updates: any;
    };
    
    // Convert date string back to Date if present
    if (updates.date) {
      updates.date = new Date(updates.date);
    }
    
    await updateExpenseInFirestore(tripId, expenseId, updates);
  } catch (error) {
    console.error('[Expenses] Error syncing update_expense:', error);
    throw error;
  }
});

syncQueue.registerHandler('delete_expense', async (action) => {
  try {
    const { tripId, expenseId } = action.payload as {
      tripId: string;
      expenseId: string;
    };

    await deleteExpenseInFirestore(tripId, expenseId);
  } catch (error) {
    console.error('[Expenses] Error syncing delete_expense:', error);
    throw error;
  }
});

/**
 * Fetch all expenses for a trip (includes offline expenses)
 */
export const fetchTripExpenses = async (tripId: string): Promise<Expense[]> => {
  try {
    const expensesRef = collection(db, 'trips', tripId, 'expenses');
    const q = query(expensesRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    
    const expenses: Expense[] = snapshot.docs.map(doc => {
      const expense = convertFirestoreExpense(doc.id, doc.data() as FirestoreExpense);
      expense.tripId = tripId;
      return expense;
    });
    
    const offlineExpenses = await getOfflineExpenses(tripId);
    mergeOfflineExpensesIntoList(expenses, offlineExpenses);
    
    // Sort by date descending
    expenses.sort((a, b) => b.date.getTime() - a.date.getTime());

    return filterExpensesPendingFirestoreDelete(tripId, expenses);
  } catch (error) {
    console.error('Error fetching expenses:', error);

    // If online fetch fails, return offline expenses
    if (!networkMonitor.isOnline()) {
      const offlineExpenses = await getOfflineExpenses(tripId);
      const mapped = offlineExpenses.map(
        ({ syncStatus, offlineId, syncedId, syncError, queuedAt, ...expense }) => expense,
      );
      return filterExpensesPendingFirestoreDelete(tripId, mapped);
    }

    return [];
  }
};

/**
 * Subscribe to real-time updates for trip expenses
 */
export const subscribeToTripExpenses = (
  tripId: string,
  callback: (expenses: Expense[]) => void
): (() => void) => {
  const expensesRef = collection(db, 'trips', tripId, 'expenses');
  
  // Try with orderBy first, but if it fails, fall back to unordered query
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;
  
  const setupSubscription = (useOrderBy: boolean = true): (() => void) | null => {
    // Don't set up subscription if already cleaned up
    if (isCleanedUp) {
      return null;
    }
    
    try {
      const q = useOrderBy 
        ? query(expensesRef, orderBy('date', 'desc'))
        : query(expensesRef);
      
      return onSnapshot(q, (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        // Defer heavy work to avoid blocking message handlers
        setTimeout(() => {
          if (isCleanedUp) {
            return;
          }
          void (async () => {
            try {
              const expenses = snapshot.docs.map(doc => {
                const expense = convertFirestoreExpense(doc.id, doc.data() as FirestoreExpense);
                expense.tripId = tripId;
                return expense;
              });
              // Keep shared budgets aligned while online: only merge local offline expenses when
              // the app is in offline-fallback mode.
              if (networkMonitor.shouldUseOfflineDataFallback()) {
                const offlineExpenses = await getOfflineExpenses(tripId);
                mergeOfflineExpensesIntoList(expenses, offlineExpenses);
              }

              // Sort manually to ensure correct order
              expenses.sort((a, b) => b.date.getTime() - a.date.getTime());
              callback(await filterExpensesPendingFirestoreDelete(tripId, expenses));
            } catch (error) {
              console.error('[Expenses] Error processing snapshot:', error);
              if (!isCleanedUp) {
                try {
                  callback([]);
                } catch (callbackError) {
                  console.error('[Expenses] Error in callback:', callbackError);
                }
              }
            }
          })();
        }, 0);
      }, (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }
        
        console.error('Error in expenses subscription:', error);
        
        // For permission errors, immediately mark as cleaned up and don't retry
        // This prevents Firestore internal state corruption
        const errCode = error && typeof error === 'object' && 'code' in error ? String((error as { code?: string }).code) : '';
        if (errCode === 'permission-denied' || errCode === 'missing-or-insufficient-permissions') {
          console.warn('[Expenses] Permission denied, cleaning up subscription to prevent state corruption');
          isCleanedUp = true;
          if (unsubscribeFn) {
            try {
              unsubscribeFn();
            } catch (unsubError) {
              // Ignore cleanup errors
            }
            unsubscribeFn = null;
          }
          return;
        }
        
        // If it's an index error, try without ordering
        if (error?.code === 'failed-precondition' && useOrderBy && !isCleanedUp) {
          console.warn('Expenses index missing, retrying without orderBy');
          const oldUnsubscribe = unsubscribeFn;
          unsubscribeFn = null; // Clear before setting up new subscription
          if (oldUnsubscribe) {
            try {
              oldUnsubscribe(); // Unsubscribe from the failed query
            } catch (unsubError) {
              // Silently ignore - subscription may already be invalid
            }
          }
          if (!isCleanedUp) {
            try {
              unsubscribeFn = setupSubscription(false);
            } catch (setupError) {
              console.error('[Expenses] Error setting up fallback subscription:', setupError);
            }
          }
          return unsubscribeFn;
        }
        
        // For other errors, call callback with empty array
        try {
          callback([]);
        } catch (callbackError) {
          console.error('[Expenses] Error in callback:', callbackError);
        }
      });
    } catch (error) {
      console.error('[Expenses] Error setting up subscription:', error);
      return null;
    }
  };
  
  try {
    unsubscribeFn = setupSubscription(true);
  } catch (error) {
    console.error('[Expenses] Fatal error setting up subscription:', error);
    unsubscribeFn = null;
  }
  
  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;
    
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
        // and logging them causes noise
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

function mergeOfflineExpensesIntoList(expenses: Expense[], offlineExpenses: OfflineExpense[]): void {
  for (const offlineExpense of offlineExpenses) {
    if (offlineExpense.syncedId && expenses.some((expense) => expense.id === offlineExpense.syncedId)) {
      continue;
    }
    if (expenses.some((expense) => expense.id === offlineExpense.id)) {
      continue;
    }
    const { syncStatus, offlineId, syncedId, syncError, queuedAt, ...expense } = offlineExpense;
    expenses.push(expense);
  }
}

/**
 * Get a single expense
 */
export const getExpense = async (tripId: string, expenseId: string): Promise<Expense | null> => {
  try {
    const expenseRef = doc(db, 'trips', tripId, 'expenses', expenseId);
    const snapshot = await getDoc(expenseRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const expense = convertFirestoreExpense(snapshot.id, snapshot.data() as FirestoreExpense);
    expense.tripId = tripId;
    return expense;
  } catch (error) {
    console.error('Error fetching expense:', error);
    return null;
  }
};

/**
 * Create expense in Firestore (internal function)
 */
async function createExpenseInFirestore(
  tripId: string,
  expense: Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    // Validate expense data
    if (!expense.amount || expense.amount <= 0) {
      throw new Error('Expense amount must be greater than 0');
    }
    
    if (!expense.currency || expense.currency.length !== 3) {
      throw new Error('Currency must be a valid 3-letter code (e.g., USD, EUR)');
    }
    
    // Fetch trip to validate date is within trip range
    const tripRef = doc(db, 'trips', tripId);
    const tripDoc = await getDoc(tripRef);
    
    if (!tripDoc.exists()) {
      throw new Error('Trip not found');
    }
    
    const tripData = tripDoc.data();
    const tripStartDate = tripData.startDate?.toDate?.() || new Date(tripData.startDate);
    const tripEndDate = tripData.endDate?.toDate?.() || new Date(tripData.endDate);
    
    // Normalize dates to compare only date portion (ignore time)
    const normalizedExpenseDate = normalizeDate(expense.date);
    const { minDate, maxDate } = getAllowedExpenseDateWindow(
      tripStartDate,
      tripEndDate
    );

    if (normalizedExpenseDate < minDate || normalizedExpenseDate > maxDate) {
      throw new Error(
        `Expense date should be between ${minDate.toLocaleDateString()} and ${maxDate.toLocaleDateString()} (up to 12 months before start and 90 days after end).`
      );
    }
    
    const expensesRef = collection(db, 'trips', tripId, 'expenses');
    const newExpenseRef = doc(expensesRef);
    
    const firestoreExpense: Record<string, any> = {
      amount: expense.amount,
      currency: expense.currency,
      category: expense.category,
      description: expense.description || '',
      date: Timestamp.fromDate(expense.date),
      // Always store provenance for transparency; default to user-entered for older flows.
      source: expense.source ?? 'user_entered',
      createdBy: expense.createdBy,
      updatedBy: expense.createdBy,
    };
    
    // Only add optional fields if they have values
    if (expense.receiptUrl) firestoreExpense.receiptUrl = expense.receiptUrl;
    if (expense.location) firestoreExpense.location = expense.location;
    if (expense.paymentMethod) firestoreExpense.paymentMethod = expense.paymentMethod;
    if (expense.merchant) firestoreExpense.merchant = expense.merchant;
    if (expense.ocrRawText) firestoreExpense.ocrRawText = expense.ocrRawText;
    if (expense.status) firestoreExpense.status = expense.status;
    if (expense.split) {
      // Debug: Log split data before saving
      console.log('[createExpense] Saving expense with split:', {
        splitType: expense.split.splitType,
        splitAmong: expense.split.splitAmong,
        paidBy: expense.split.paidBy,
        splitAmounts: expense.split.splitAmounts,
        fullSplit: expense.split,
      });
      firestoreExpense.split = expense.split;
    }

    // If expense currency differs from trip's base currency, capture conversion rate
    const tripBaseCurrency = (tripData.baseCurrency || 'USD').toUpperCase();
    const expenseCurrency = expense.currency.toUpperCase();
    
    if (expenseCurrency !== tripBaseCurrency) {
      try {
        const exchangeRate = await getExchangeRate(expenseCurrency, tripBaseCurrency);
        const convertedAmount = expense.amount * exchangeRate;
        
        firestoreExpense.conversion = {
          fromCurrency: expenseCurrency,
          toCurrency: tripBaseCurrency,
          exchangeRate,
          convertedAmount,
          conversionDate: Timestamp.now(),
        };
        
        console.log(`[createExpense] Captured conversion: ${expense.amount} ${expenseCurrency} = ${convertedAmount.toFixed(2)} ${tripBaseCurrency} (rate: ${exchangeRate})`);
      } catch (conversionError) {
        console.warn('[createExpense] Failed to capture conversion rate:', conversionError);
        // Continue without conversion data - it will be calculated on-the-fly
      }
    }
    
    await setDoc(newExpenseRef, {
      ...firestoreExpense,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return newExpenseRef.id;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
}

/**
 * Create a new expense (with offline support)
 */
export const createExpense = async (
  tripId: string,
  expense: Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  // Check if offline
  if (!networkMonitor.isOnline()) {
    console.log('[Expenses] Device is offline, storing expense locally');
    const offlineId = await addOfflineExpense(tripId, expense);
    return offlineId;
  }

  // Online - create in Firestore
  try {
    return await createExpenseInFirestore(tripId, expense);
  } catch (error) {
    // If online creation fails, fall back to offline storage
    console.warn('[Expenses] Online creation failed, falling back to offline storage:', error);
    const offlineId = await addOfflineExpense(tripId, expense);
    return offlineId;
  }
};

/**
 * Update expense in Firestore (internal function)
 */
async function updateExpenseInFirestore(
  tripId: string,
  expenseId: string,
  updates: Partial<Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'createdBy'>>
): Promise<void> {
  try {
    // Validate expense date if being updated
    if (updates.date !== undefined) {
      const tripRef = doc(db, 'trips', tripId);
      const tripDoc = await getDoc(tripRef);
      
      if (!tripDoc.exists()) {
        throw new Error('Trip not found');
      }
      
      const tripData = tripDoc.data();
      const tripStartDate = tripData.startDate?.toDate?.() || new Date(tripData.startDate);
      const tripEndDate = tripData.endDate?.toDate?.() || new Date(tripData.endDate);
      
      const normalizedExpenseDate = normalizeDate(updates.date);
      const { minDate, maxDate } = getAllowedExpenseDateWindow(tripStartDate, tripEndDate);

      if (normalizedExpenseDate < minDate || normalizedExpenseDate > maxDate) {
        throw new Error(
          `Expense date should be between ${minDate.toLocaleDateString()} and ${maxDate.toLocaleDateString()} (up to 12 months before start and 90 days after end).`
        );
      }
    }
    
    // Validate amount if being updated
    if (updates.amount !== undefined && updates.amount <= 0) {
      throw new Error('Expense amount must be greater than 0');
    }
    
    // Validate currency if being updated
    if (updates.currency !== undefined && updates.currency.length !== 3) {
      throw new Error('Currency must be a valid 3-letter code');
    }
    
    const expenseRef = doc(db, 'trips', tripId, 'expenses', expenseId);
    
    const firestoreUpdates: Record<string, any> = {};
    
    // Only add fields that are explicitly provided
    if (updates.amount !== undefined) firestoreUpdates.amount = updates.amount;
    if (updates.currency !== undefined) firestoreUpdates.currency = updates.currency;
    if (updates.category !== undefined) firestoreUpdates.category = updates.category;
    if (updates.description !== undefined) firestoreUpdates.description = updates.description || '';
    if (updates.date !== undefined) firestoreUpdates.date = Timestamp.fromDate(updates.date);
    if (updates.receiptUrl !== undefined) firestoreUpdates.receiptUrl = updates.receiptUrl || null;
    if (updates.location !== undefined) firestoreUpdates.location = updates.location || null;
    if (updates.paymentMethod !== undefined) firestoreUpdates.paymentMethod = updates.paymentMethod || null;
    if (updates.merchant !== undefined) firestoreUpdates.merchant = updates.merchant || null;
    if ((updates as any).source !== undefined) firestoreUpdates.source = (updates as any).source || 'user_entered';
    if (updates.status !== undefined) firestoreUpdates.status = updates.status;
    if (updates.split !== undefined) firestoreUpdates.split = updates.split || null;
    if ((updates as any).updatedBy !== undefined) firestoreUpdates.updatedBy = (updates as any).updatedBy || null;
    
    await setDoc(expenseRef, {
      ...firestoreUpdates,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
}

/**
 * Update an existing expense (with offline support)
 */
export const updateExpense = async (
  tripId: string,
  expenseId: string,
  updates: Partial<Omit<Expense, 'id' | 'tripId' | 'createdBy' | 'createdAt' | 'updatedAt'>>
): Promise<void> => {
  // Check if this is an offline expense
  const offlineExpenses = await getOfflineExpenses(tripId);
  const offlineExpense = offlineExpenses.find(e => e.id === expenseId || e.offlineId === expenseId);

  if (offlineExpense) {
    // Update offline expense
    await updateOfflineExpense(tripId, offlineExpense.id, updates);
    return;
  }

  // Check if offline
  if (!networkMonitor.isOnline()) {
    console.log('[Expenses] Device is offline, updating expense locally');
    const expense = await getExpense(tripId, expenseId);
    if (expense) {
      await seedFirestoreExpenseForOfflineUpdate(tripId, expense);
      await updateOfflineExpense(tripId, expenseId, updates);
    }
    return;
  }

  // Online - update in Firestore
  try {
    await updateExpenseInFirestore(tripId, expenseId, updates);
  } catch (error) {
    // If online update fails, fall back to offline storage
    console.warn('[Expenses] Online update failed, falling back to offline storage:', error);
    const expense = await getExpense(tripId, expenseId);
    if (expense) {
      await seedFirestoreExpenseForOfflineUpdate(tripId, expense);
      await updateOfflineExpense(tripId, expenseId, updates);
    } else {
      throw error;
    }
  }
};

/**
 * Delete expense in Firestore (internal function)
 */
async function deleteExpenseInFirestore(tripId: string, expenseId: string): Promise<void> {
  console.log('[Expenses] deleteExpenseInFirestore called');
  try {
    const expenseRef = doc(db, 'trips', tripId, 'expenses', expenseId);
    console.log('[Expenses] Attempting to delete expense document');
    await deleteDoc(expenseRef);
    await removePendingFirestoreExpenseDelete(tripId, expenseId);
    console.log('[Expenses] Successfully deleted expense');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Expenses] Error deleting expense', { message });
    throw error;
  }
}

/**
 * Delete an expense (with offline support)
 */
export const deleteExpense = async (tripId: string, expenseId: string): Promise<void> => {
  // Check if this is an offline expense
  const offlineExpenses = await getOfflineExpenses(tripId);
  const offlineExpense = offlineExpenses.find(e => e.id === expenseId || e.offlineId === expenseId);

  if (offlineExpense) {
    // Delete offline expense
    await deleteOfflineExpense(tripId, offlineExpense.id);
    return;
  }

  // Check if offline
  if (!networkMonitor.isOnline()) {
    console.log('[Expenses] Device is offline, marking expense for deletion');
    await addPendingFirestoreExpenseDelete(tripId, expenseId);
    // Queue for deletion when online
    await syncQueue.queueAction({
      type: 'delete_expense',
      payload: {
        tripId,
        expenseId,
      },
      tripId,
    });
    return;
  }

  // Online - delete in Firestore
  try {
    await deleteExpenseInFirestore(tripId, expenseId);
  } catch (error) {
    console.error('[Expenses] Error deleting expense:', error);
    throw error;
  }
};

/**
 * Calculate total expenses by category
 */
export const calculateExpensesByCategory = (
  expenses: Expense[],
  baseCurrency: string = 'USD'
): Record<string, { amount: number; count: number }> => {
  const totals: Record<string, { amount: number; count: number }> = {};
  
  expenses.forEach(expense => {
    const category = expense.category;
    if (!totals[category]) {
      totals[category] = { amount: 0, count: 0 };
    }
    
    // For now, just sum amounts (currency conversion will be added later)
    // Deferred post-launch: convert to baseCurrency once FX conversion service ships.
    totals[category].amount += expense.amount;
    totals[category].count += 1;
  });
  
  return totals;
};

/**
 * Calculate total expenses for a trip
 */
export const calculateTotalExpenses = (
  expenses: Expense[],
  baseCurrency: string = 'USD'
): number => {
  // For now, just sum amounts (currency conversion will be added later)
  // Deferred post-launch: convert to baseCurrency once FX conversion service ships.
  return expenses.reduce((total, expense) => total + expense.amount, 0);
};

